/**
 * Tree hit areas on `bird_trees.jpg` (logical 1920×1080).
 * Tuned via player1 dev drag/resize editor; copy from layout export when updating.
 */

export const BEYOND_BIRD_MEMORY_LOGICAL_WIDTH = 1920;
export const BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT = 1080;

/** Dev-only (player1): overlay grid while tuning tree coordinates. Set false when coords are final. */
export const SHOW_BEYOND_BIRD_MINIGAME_GRID = false;

/**
 * @typedef {{ id: number, center: [number, number], hitRadius: number }} BeyondBirdTreeSlot
 */

/** @type {BeyondBirdTreeSlot[]} */
export const BEYOND_BIRD_MEMORY_TREE_SLOTS = [
  { id: 0, center: [256, 715], hitRadius: 41 },
  { id: 1, center: [617, 746], hitRadius: 34 },
  { id: 2, center: [988, 676], hitRadius: 52 },
  { id: 3, center: [1335, 741], hitRadius: 35 },
  { id: 4, center: [1694, 720], hitRadius: 39 },
];

/** @returns {BeyondBirdTreeSlot[]} */
export function getDefaultBeyondBirdTreeSlots() {
  return BEYOND_BIRD_MEMORY_TREE_SLOTS.map((s) => ({
    id: s.id,
    center: [s.center[0], s.center[1]],
    hitRadius: s.hitRadius,
  }));
}

export const BEYOND_BIRD_MEMORY_BACKGROUND_SRC = '/minigames/bird_trees.jpg';

export const BEYOND_BIRD_MEMORY_APPEAR_SOUND_SRC = '/minigames/bird_appear2.mp3';

/** Target wall-clock duration for the pop sequence before recall (~30s). */
export const BEYOND_BIRD_MEMORY_SEQUENCE_TARGET_MS = 30000;

/**
 * Per-step delays: starts slow, accelerates toward the last pop.
 * @param {number} totalSteps
 * @returns {{ steps: { gapBeforeMs: number, displayMs: number }[], recallTransitionMs: number }}
 */
export function getBeyondBirdMemoryAcceleratingStepTimings(totalSteps) {
  if (!Number.isInteger(totalSteps) || totalSteps < 1) {
    throw new Error(`getBeyondBirdMemoryAcceleratingStepTimings: invalid totalSteps (${String(totalSteps)})`);
  }
  const initialDelayMs = 450;
  const recallTransitionMs = 280;
  const budgetMs = BEYOND_BIRD_MEMORY_SEQUENCE_TARGET_MS - initialDelayMs - recallTransitionMs;

  const weights = [];
  for (let i = 0; i < totalSteps; i += 1) {
    const progress = totalSteps <= 1 ? 0 : i / (totalSteps - 1);
    weights.push(1.35 - progress * 0.85);
  }
  const weightSum = weights.reduce((acc, w) => acc + w, 0);

  const steps = weights.map((weight, i) => {
    const sliceMs = (budgetMs * weight) / weightSum;
    const displayMs = Math.max(280, Math.round(sliceMs * 0.58));
    const gapBeforeMs = i === 0 ? initialDelayMs : Math.max(90, Math.round(sliceMs * 0.42));
    return { gapBeforeMs, displayMs };
  });

  return { steps, recallTransitionMs };
}

/**
 * @param {number} birdId 1–7
 * @returns {string}
 */
export function getBeyondBirdImageSrc(birdId) {
  if (!Number.isInteger(birdId) || birdId < 1 || birdId > 7) {
    throw new Error(`getBeyondBirdImageSrc: invalid birdId (${String(birdId)})`);
  }
  return `/minigames/bird${birdId}.png`;
}
